/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form } from 'react-bootstrap';
import {
  useForm,
  Controller,
  FieldValues,
  SubmitHandler
} from 'react-hook-form';
import { useQuery } from 'react-query';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';

import Button from 'components/base/Button';
import cashboxService from 'service/cashbox';
import { pageToOffset } from 'utils/pageToOffset';
import usersService from 'service/users';
import legalEntityCurrencyService from 'service/legalEntityCurrency';
import documentStatusService from 'service/documentStatus';

interface AllChecksFilterProps {
  currentPage: number;
  setPageCount: (pageCount: number) => void;
  setSavedFilterValues: (values: any) => void;
  setOpenFilter: (open: boolean) => void;
  setTableData: (data: any) => void;
  dateFrom?: string;
  dateTo?: string;
  dataSettings?: any;
  savedFilterValues?: any;
}
const AllChecksFilter = ({
  currentPage,
  setPageCount,
  setSavedFilterValues,
  setOpenFilter,
  setTableData,
  dateFrom,
  dateTo,
  dataSettings,
  savedFilterValues
}: AllChecksFilterProps) => {
  const { t, i18n } = useTranslation();

  const { control, handleSubmit, reset, setValue, watch, register } = useForm();

  const [loading, setLoading] = useState(false);
  const [cashersOption, setCashersOption] = useState<any>([]);
  const [usersOption, setUsersOption] = useState<any>([]);
  const [currencyOption, setCurrencyOption] = useState<any>([]);
  const [statusOption, setStatusOption] = useState<any>([]);

  //GET UNITS to options
  useQuery(['GET_CASHIER'], async () => {
    await usersService
      .getListCashier({ offset: 0, limit: 100 })
      .then((res: any) => {
        const options = res?.data?.users?.map((option: any) => ({
          value: option?.id,
          label: option?.first_name + ' ' + option?.last_name
        }));
        setCashersOption(options);
      });
  });

  //GET Currency Entity to options
  useQuery(['GET_USERS'], async () => {
    await usersService.getList({ offset: 0, limit: 100 }).then((res: any) => {
      const options = res?.data?.users?.map((option: any) => ({
        value: option?.id,
        label: option?.first_name + ' ' + option?.last_name
      }));
      setUsersOption(options);
    });
  });

  //GET Currency Entity to options
  useQuery(['GET_CURRENCY'], async () => {
    await legalEntityCurrencyService
      .getList({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.legal_entity_currencies?.map((option: any) => ({
          value: option?.currency?.id,
          label: option?.currency?.name?.[i18n?.language]
        }));
        setCurrencyOption(options);
      });
  });

  //GET Currency Entity to options
  useQuery(['GET_DOCUMENT_STATUS'], async () => {
    await documentStatusService
      .getList({ offset: 0, limit: 100, status: true, show_receipt: true })
      .then((res: any) => {
        const options = res?.document_statuses?.map((option: any) => ({
          value: option?.id,
          label: option?.name?.[i18n?.language]
        }));
        setStatusOption(options);
      });
  });

  /* prettier-ignore */
  const formatDateToTimeZone = (date: number, timeZone: string) => {
    try {
      // Format the date according to the specified time zone
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timeZone
      }).format(new Date(date));
    } catch (error) {
      console.error('Error formatting date with time zone:', timeZone, error);
      return ''; // Return empty string if the time zone is invalid
    }
  };

  //Get by id
  const fetchData = () => {
    if (savedFilterValues === '') return false;

    savedFilterValues?.map((el: any) => {
      const computed = {
        receipt_number: el?.receipt_number,
        amount_from: el?.amount_from,
        amount_to: el?.amount_to,
        users: el?.employee_id,
        buyer: el?.customer_id,
        status: el?.status_id,
        currency: el?.currency_id
      };
      reset(computed);
    });
  };

  const onSubmit: SubmitHandler<FieldValues> = data => {
    setLoading(true);

    let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

    timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();

    const params = {
      receipt_number: data?.receipt_number
        ? Number(data?.receipt_number)
        : undefined,
      amount_from: Number(data?.amount_from) || undefined,
      amount_to: Number(data?.amount_to) || undefined,
      employee_id: data?.users ? data?.users : undefined,
      customer_id: data?.buyer ? data?.buyer : undefined,
      currency_id: data?.currency ? data?.currency : undefined,
      status_id: data?.status ? data?.status : undefined
    };

    cashboxService
      ?.getReceipt({
        limit: 10,
        offset: pageToOffset(currentPage, 10),
        date_from: dateFrom,
        date_to: dateTo,
        ...params
      })
      .then((res: any) => {
        if (res) {
          setPageCount(Math.ceil(res?.count / 10));
          setLoading(false);

          const resData =
            res?.receipts?.map((items: any) => {
              return {
                buyer: items?.customer
                  ? items?.customer?.first_name +
                    ' ' +
                    items?.customer?.last_name
                  : '',
                check_number: items?.receipt_number,
                currency: items?.currency?.name?.[i18n?.language],
                check_sum: items?.amount
                  ? new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 4,
                      useGrouping: true
                    })
                      .format(items?.amount)
                      .replace(/,/g, ' ')
                  : 0,
                cashier: `${items?.user?.first_name} ${items?.user?.last_name}`,
                date_create: formatDateToTimeZone(items?.date_create, timeZone),
                date_sync: formatDateToTimeZone(items?.date_sync, timeZone),
                status: items?.status?.name?.[i18n?.language],
                guid: items?.id
              };
            }) ?? [];

          if (resData) {
            setTableData(resData);
            setSavedFilterValues([
              {
                receipt_number: data?.receipt_number
                  ? data?.receipt_number
                  : undefined,
                amount_from: data?.amount_from ? data?.amount_from : undefined,
                amount_to: data?.amount_to ? data?.amount_to : undefined,
                employee_id: data?.users ? data?.users : undefined,
                customer_id: data?.buyer ? data?.buyer : undefined,
                currency_id: data?.currency ? data?.currency : undefined,
                status_id: data?.status ? data?.status : undefined
              }
            ]);
            setOpenFilter(false);
            setLoading(false);
          }
        }
      });
  };

  const handleClear = () => {
    let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

    timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();

    cashboxService
      ?.getReceipt({
        limit: 10,
        offset: pageToOffset(currentPage, 10),
        date_from: dateFrom,
        date_to: dateTo
      })
      .then((res: any) => {
        if (res) {
          setPageCount(Math.ceil(res?.count / 10));
          setLoading(false);

          const resData =
            res?.receipts?.map((items: any) => {
              return {
                buyer: items?.customer
                  ? items?.customer?.first_name +
                    ' ' +
                    items?.customer?.last_name
                  : '',
                check_number: items?.receipt_number,
                currency: items?.currency?.name?.[i18n?.language],
                check_sum: items?.amount
                  ? new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 4,
                      useGrouping: true
                    })
                      .format(items?.amount)
                      .replace(/,/g, ' ')
                  : 0,
                cashier: `${items?.user?.first_name} ${items?.user?.last_name}`,
                date_create: formatDateToTimeZone(items?.date_create, timeZone),
                date_sync: formatDateToTimeZone(items?.date_sync, timeZone),
                status: items?.status?.name?.[i18n?.language],
                guid: items?.id
              };
            }) ?? [];

          if (resData) {
            setTableData(resData);
            reset({
              receipt_number: '',
              amount_from: '',
              amount_to: '',
              users: '',
              buyer: '',
              currency: '',
              status: ''
            });
            setSavedFilterValues([]);
            setOpenFilter(false);
            setLoading(false);
          }
        }
      });
  };

  useEffect(() => {
    if (currencyOption?.length === 1) {
      setValue('currency', currencyOption[0].value);
    }
  }, [currencyOption, setValue]);

  useEffect(() => {
    if (savedFilterValues?.length > 0) {
      fetchData();
    }
  }, [savedFilterValues]);

  return (
    <>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          gap: '10px'
        }}
      >
        <Form.Floating>
          <Form.Control
            {...register('receipt_number')}
            type="text"
            placeholder={t('check_number')}
            onFocus={e => e.target.select()}
            autoComplete="off"
          />
          <label htmlFor="floatingInputCustom">{t('check_number')}</label>
        </Form.Floating>

        <Form.Group>
          <div className="react-select-container position-relative">
            <Controller
              name="users"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Form.Floating>
                  <Form.Select {...field}>
                    <option className="d-none" value=""></option>
                    {cashersOption?.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>

                  <label htmlFor="floatingInputCustom">{t('cashiers')}</label>
                  {watch('users') !== '' && (
                    <Button
                      variant="link"
                      style={{
                        position: 'absolute',
                        right: '5%',
                        top: '15%',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setValue('users', '');
                      }}
                    >
                      <FeatherIcon icon="delete" size={16} />
                    </Button>
                  )}
                </Form.Floating>
              )}
            />
          </div>
        </Form.Group>

        <Form.Group>
          <div className="react-select-container position-relative">
            <Controller
              name="buyer"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Form.Floating>
                  <Form.Select {...field}>
                    <option className="d-none" value=""></option>
                    {usersOption?.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>

                  <label htmlFor="floatingInputCustom">{t('buyer')}</label>
                  {watch('buyer') !== '' && (
                    <Button
                      variant="link"
                      style={{
                        position: 'absolute',
                        right: '5%',
                        top: '15%',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setValue('buyer', '');
                      }}
                    >
                      <FeatherIcon icon="delete" size={16} />
                    </Button>
                  )}
                </Form.Floating>
              )}
            />
          </div>
        </Form.Group>

        <Form.Group>
          <div className="react-select-container position-relative">
            <Controller
              name="currency"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Form.Floating>
                  <Form.Select {...field}>
                    <option className="d-none" value=""></option>
                    {currencyOption?.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>

                  <label htmlFor="floatingInputCustom">{t('currency')}</label>
                  {watch('currency') !== '' && (
                    <Button
                      variant="link"
                      style={{
                        position: 'absolute',
                        right: '5%',
                        top: '15%',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setValue('currency', '');
                      }}
                    >
                      <FeatherIcon icon="delete" size={16} />
                    </Button>
                  )}
                </Form.Floating>
              )}
            />
          </div>
        </Form.Group>

        <Form.Group>
          <div className="react-select-container position-relative">
            <Controller
              name="status"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Form.Floating>
                  <Form.Select {...field}>
                    <option className="d-none" value=""></option>
                    {statusOption?.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>

                  <label htmlFor="floatingInputCustom">{t('status')}</label>
                  {watch('status') !== '' && (
                    <Button
                      variant="link"
                      style={{
                        position: 'absolute',
                        right: '5%',
                        top: '15%',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setValue('status', '');
                      }}
                    >
                      <FeatherIcon icon="delete" size={16} />
                    </Button>
                  )}
                </Form.Floating>
              )}
            />
          </div>
        </Form.Group>

        <Form.Floating>
          <Form.Control
            {...register('amount_from')}
            type="text"
            placeholder={t('amount_from')}
            onFocus={e => e.target.select()}
            autoComplete="off"
          />
          <label htmlFor="floatingInputCustom">{t('amount_from')}</label>
        </Form.Floating>

        <Form.Floating>
          <Form.Control
            {...register('amount_to')}
            type="text"
            placeholder={t('amount_to')}
            onFocus={e => e.target.select()}
            autoComplete="off"
          />
          <label htmlFor="floatingInputCustom">{t('amount_to')}</label>
        </Form.Floating>

        <Form.Group style={{ display: 'flex', gap: '10px', marginTop: 'auto' }}>
          <Button
            style={{ height: '48px', width: '100%' }}
            variant="outline-primary"
            type="button"
            onClick={() => handleClear()}
          >
            {t('reset')}
          </Button>
          <Button
            style={{ height: '48px', width: '100%' }}
            variant="primary"
            type="submit"
            loading={loading}
            loadingPosition="start"
          >
            {t('apply')}
          </Button>
        </Form.Group>
      </Form>
    </>
  );
};

export default AllChecksFilter;
